
import React, { useState, useRef, useEffect } from 'react';

interface WikiHeaderProps {
  title: string;
  category?: string;
  onBack?: () => void; // Explicit Back Action
  onMenuClick?: () => void; // Explicit Menu Toggle
  error?: string | null;
  menuIcon?: boolean; // Deprecated/Legacy flag, can be used to force showing menu button if onMenuClick provided
  isSidebarOpen?: boolean;
  breadcrumbRootLabel?: string;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (val: string) => void;
  searchPlaceholder?: string;
  children?: React.ReactNode;
  centerContent?: React.ReactNode;
  stats?: React.ReactNode;
  saveStatus?: 'saved' | 'saving' | 'unsaved' | 'error';
  isAdmin?: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
  rightAction?: React.ReactNode;
  languageSelector?: React.ReactNode;
}

const WikiHeader: React.FC<WikiHeaderProps> = ({
  title,
  onBack,
  onMenuClick,
  menuIcon = false, // Kept for compat, but logic relies on onMenuClick presence now
  isSidebarOpen = false,
  breadcrumbRootLabel = "Wiki",
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = "Filter archives...",
  children,
  centerContent,
  stats,
  saveStatus,
  isAdmin,
  onSync,
  isSyncing,
  rightAction,
  languageSelector
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const handleCloseSearch = () => {
    setIsSearchExpanded(false);
    if (onSearchChange && searchQuery) {
        onSearchChange(''); 
    }
  };

  return (
    <div className="h-16 border-b border-stone-200 dark:border-[#1e293b] bg-[#f2efe4] dark:bg-[#0f1521] flex items-center px-4 shrink-0 z-[30] gap-4 shadow-sm relative transition-colors duration-300 w-full">
      {/* --- LEFT SECTION --- */}
      <div className={`flex items-center gap-3 shrink-0 ${isSearchExpanded ? 'hidden' : 'flex'}`}>
        
        {onMenuClick && (
            <button 
                onClick={onMenuClick} 
                className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded bg-[#fbfaf6] dark:bg-[#1a2333] border border-stone-200 dark:border-[#1e293b] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 shadow-sm transition-all shrink-0 ${isSidebarOpen ? 'text-amber-500 border-amber-200 bg-amber-50 dark:bg-amber-900/10' : ''}`}
                title={isSidebarOpen ? "Close Menu" : "Open Menu"}
            >
                <i className={`fa-solid ${isSidebarOpen ? 'fa-angles-left text-xs' : 'fa-bars text-sm'}`}></i>
            </button>
        )}

        {onBack && (
            <button 
                onClick={onBack} 
                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded bg-[#fbfaf6] dark:bg-[#1a2333] border border-stone-200 dark:border-[#1e293b] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 shadow-sm transition-all shrink-0"
                title="Back"
            >
                <i className="fa-solid fa-arrow-left text-sm"></i>
            </button>
        )}

        <div className="flex flex-col min-w-0">
           <span className="text-[0.5625rem] uppercase font-bold text-stone-400 tracking-[0.2em] leading-none mb-1">
             {breadcrumbRootLabel}
           </span>
           <span className="font-bold text-stone-800 dark:text-stone-100 truncate text-sm leading-tight max-w-[120px] md:max-w-[200px]" title={title}>
             {title}
           </span>
        </div>
      </div>
      
      {/* --- CENTER SECTION --- */}
      {showSearch ? (
        <div className={`flex-1 flex justify-center w-full px-2 ${isSearchExpanded ? 'absolute inset-0 z-50 bg-[#f2efe4] dark:bg-[#0f1521] px-4' : ''}`}>
            <div className={`flex items-center gap-2 w-full max-w-xl ${isSearchExpanded ? 'h-full' : 'justify-end'}`}>
                {isSearchExpanded && (
                    <button onClick={handleCloseSearch} className="w-8 h-8 flex items-center justify-center text-stone-500 mr-2">
                        <i className="fa-solid fa-arrow-left"></i>
                    </button>
                )}
                <div className="relative flex-1 flex justify-end">
                    {!isSearchExpanded ? (
                        <button 
                            onClick={() => setIsSearchExpanded(true)}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#fdfbf7] dark:bg-[#1a2333] border border-stone-200 dark:border-[#1e293b] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 shadow-sm flex"
                        >
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </button>
                    ) : (
                        <div className="block w-full relative animate-in fade-in zoom-in-95 duration-200">
                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs pointer-events-none"></i>
                            <input 
                                ref={inputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="w-full pl-9 pr-4 h-10 bg-[#fdfbf7] dark:bg-[#1a2333] text-stone-800 dark:text-white border border-stone-200 dark:border-[#1e293b] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all placeholder:text-stone-400 shadow-inner"
                            />
                            <button onClick={handleCloseSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 p-1">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
      ) : centerContent ? (
        <div className="flex-1 flex justify-end md:justify-center items-center px-2">
            {centerContent}
        </div>
      ) : (
        <div className="flex-1"></div>
      )}

      {/* --- RIGHT SECTION --- */}
      <div className={`flex items-center gap-2 shrink-0 ${isSearchExpanded ? 'hidden' : 'flex'}`}>
         {languageSelector && <div className="md:hidden flex items-center">{languageSelector}</div>}
         <div className="flex items-center gap-2">{children}</div>
         
         {onSync && (
            <button 
                onClick={onSync}
                disabled={isSyncing}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all shadow-sm ${
                    saveStatus === 'unsaved' 
                        ? 'bg-amber-500 text-white hover:bg-amber-600 animate-pulse' 
                        : saveStatus === 'error'
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:border-amber-500 hover:text-amber-500'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                <i className={`fa-solid ${isSyncing ? 'fa-rotate animate-spin' : (saveStatus === 'error' ? 'fa-triangle-exclamation' : (saveStatus === 'saved' ? 'fa-check' : 'fa-cloud-arrow-up'))}`}></i>
                <span className="hidden md:inline">{isSyncing ? 'Syncing...' : (saveStatus === 'unsaved' ? 'Save Changes' : (saveStatus === 'error' ? 'Retry Sync' : 'Synced'))}</span>
            </button>
         )}

         {stats && <div className="flex items-center gap-3 pl-4 border-l border-stone-200 dark:border-[#1e293b]">{stats}</div>}
         
         {saveStatus && !onSync && (
            <div className="hidden sm:block pl-4 border-l border-stone-200 dark:border-[#1e293b]">
                <span className={`text-[0.625rem] font-mono transition-colors uppercase px-2 py-1 rounded flex items-center gap-1 ${
                    saveStatus === 'saved' ? 'bg-stone-200 dark:bg-stone-800 text-green-500' : 
                    saveStatus === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-500' : 'bg-stone-200 dark:bg-stone-800 text-amber-500'
                }`}>
                    {saveStatus === 'error' && <i className="fa-solid fa-circle-exclamation"></i>}
                    {saveStatus === 'error' ? 'Sync Failed' : saveStatus}
                </span>
            </div>
         )}

         {rightAction && (
             <div className="pl-3 ml-1 border-l border-stone-200 dark:border-[#1e293b]">
                 {rightAction}
             </div>
         )}
      </div>
    </div>
  );
};

export default WikiHeader;
